import "./SubmissionWindow.css";
import { useState } from "react";
import Card from "../Card/Card";
import TextField from "../TextField/TextField";
import SelectField from "../SelectField/SelectField";
import Button from "../Button/Button";

export default function SubmissionWindow(props) {
  const { window, value, remaining } = props;

  const [draft, setDraft] = useState(value ? value.url : "");
  const [answers, setAnswers] = useState(value ? { ...value.answers } : {});
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [busy, setBusy] = useState(false);

  const submitted = Boolean(value);
  const showForm = !submitted || editing;

  const clock = remaining ? (
    <div className="window-clock col">
      <span className="window-clock-value t-mono">{remaining}</span>
      <span className="window-clock-label t-body-sm t-dimmer">Window closes</span>
    </div>
  ) : null;

  if (!window) {
    return (
      <Card className="window">
        <div className="window-head row">
          <div className="window-id col">
            <span className="window-eyebrow t-eyebrow">Right now</span>
            <h2 className="window-title t-h2">Nothing to submit</h2>
          </div>
        </div>
        {props.note ? (
          <div className="window-idle col">
            <p className="t-body-sm t-dim">{props.note}</p>
          </div>
        ) : null}
      </Card>
    );
  }

  async function submit() {
    setBusy(true);
    setError("");
    setFieldErrors({});

    const nextFieldErrors = {};
    for (const input of window.inputs || []) {
      const raw = answers[input.key];
      if (input.required && (!raw || !String(raw).trim())) {
        nextFieldErrors[input.key] = "This field is required.";
      }
    }
    if (Object.keys(nextFieldErrors).length) {
      setFieldErrors(nextFieldErrors);
      setBusy(false);
      return;
    }

    try {
      await props.onSubmit(window.slot, window.kind, draft, answers);
      setEditing(false);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  function answerLabel(input) {
    const raw = answers[input.key] || (value && value.answers && value.answers[input.key]);
    const option = (input.options || []).find((o) => o.value === raw);
    return option ? option.label : raw;
  }

  return (
    <Card className="window">
      <div className="window-head row">
        <div className="window-id col">
          <span className="window-eyebrow t-eyebrow">Due now</span>
          <h2 className="window-title t-h2">{window.title}</h2>
        </div>
        {clock}
      </div>

      <div className="window-task col">
        <div className="window-task-head row">
          <span className={`window-kind window-kind-${window.kind}`}>{window.badge}</span>
          <div className="window-task-id col">
            {window.brief ? (
              <span className="window-task-brief t-body-sm t-dim">{window.brief}</span>
            ) : null}
          </div>
          {submitted && !showForm ? (
            <span className="window-state t-label">Submitted</span>
          ) : null}
        </div>

        {submitted && !showForm ? (
          <div className="window-value row">
            <a
              className="window-link t-mono"
              href={value.url}
              target="_blank"
              rel="noreferrer noopener"
            >
              {value.url.replace(/^https?:\/\//, "")}
            </a>
            <span className="window-at t-mono">{value.at}</span>
            {(window.inputs || []).some((input) => answerLabel(input)) ? (
              <div className="window-answers">
                {window.inputs
                  .filter((input) => answerLabel(input))
                  .map((input) => (
                    <span key={input.key} className="window-answer">
                      <span className="window-answer-label">{input.label}: </span>
                      {answerLabel(input)}
                    </span>
                  ))}
              </div>
            ) : null}
            <Button
              variant="quiet"
              onClick={() => {
                setDraft(value.url);
                setAnswers({ ...value.answers });
                setEditing(true);
              }}
            >
              Edit
            </Button>
          </div>
        ) : null}

        {showForm ? (
          <>
            <TextField
              label={window.label}
              value={draft}
              onChange={(v) => {
                setDraft(v);
                if (error) setError("");
              }}
              onSubmit={submit}
              placeholder={window.placeholder}
              help={window.help}
              error={error}
              mono
            />

            {(window.inputs || []).length ? (
              <div className="window-inputs col">
                {window.inputs.map((input) => (
                  <SelectField
                    key={input.key}
                    label={input.label}
                    help={fieldErrors[input.key] ? undefined : input.help}
                    error={fieldErrors[input.key]}
                    required={input.required}
                    placeholder={input.placeholder}
                    options={input.options}
                    value={answers[input.key] || ""}
                    onChange={(v) => {
                      setAnswers((prev) => ({ ...prev, [input.key]: v }));
                      if (fieldErrors[input.key]) {
                        setFieldErrors((prev) => {
                          const next = { ...prev };
                          delete next[input.key];
                          return next;
                        });
                      }
                    }}
                  />
                ))}
              </div>
            ) : null}

            <div className="window-actions row">
              <Button onClick={submit} busy={busy} busyLabel="Checking link…">
                {submitted ? "Update link" : window.cta}
              </Button>
              {submitted ? (
                <Button
                  variant="ghost"
                  onClick={() => {
                    setEditing(false);
                    setError("");
                    setFieldErrors({});
                  }}
                >
                  Cancel
                </Button>
              ) : null}
              {window.templateUrl ? (
                <a
                  className="btn btn-ghost window-template"
                  href={window.templateUrl}
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  <span className="btn-label">{window.templateLabel || "View template"}</span>
                  <span className="window-template-arrow" aria-hidden="true">↗</span>
                  <span className="sr-only">(opens in a new tab)</span>
                </a>
              ) : null}
            </div>
          </>
        ) : null}
      </div>

      {window.note ? <p className="window-note t-body-sm t-dimmer">{window.note}</p> : null}
    </Card>
  );
}
